import mapValues from 'lodash/mapValues';

import { OCCUPANCY_TOKEN, occupancyOf } from '../tabs/packageOptions/previousPackageVariants';

import type { SheetContext } from './parsePricingSheet';
import { cellAddress, cellAt, findLabel, textOf } from './sheetCells';
import type { ParsedDealOption } from './types';

const COLUMNS = {
  name: 'Deal Option Name',
  sell: 'Sell Price (AUD)',
  nights: 'Number of Nights',
  roomType: 'Room Type',
  net: 'Cost Price',
  extraNightNet: 'Nightly Cost Price',
  extraNightSell: 'Nightly Sell Price (AUD)',
  allocation: 'Allocation Per Day',
} as const;

type Column = keyof typeof COLUMNS;

const LEADING_OCCUPANCY = new RegExp(`^${OCCUPANCY_TOKEN}`, 'i');

export interface ParsedDealOptions {
  dealOptions: Array<ParsedDealOption>;
  skippedVariants: Array<string>;
}

function numberOf(value: unknown): number | null {
  const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof number === 'number' && Number.isFinite(number) ? number : null;
}

function positiveNumberOf(value: unknown): number | null {
  const number = numberOf(value);
  return number !== null && number > 0 ? number : null;
}

// The grid shows and edits whole dollars, so a fraction would sit unseen under every margin.
function priceOf(value: unknown): number | null {
  const number = positiveNumberOf(value);
  return number === null ? null : Math.round(number) || null;
}

const squash = (text: string) => text.replace(/\s+/g, '').toLowerCase();

// Whitespace and case are ignored on both sides, so "5N Pool Villa" still matches a name typed "5N PoolVilla".
function textAfterPrefix(name: string, prefix: string): string | null {
  const wanted = squash(prefix);
  let matched = 0;
  let index = 0;
  while (matched < wanted.length) {
    if (index >= name.length) {
      return null;
    }
    const char = name[index].toLowerCase();
    index += 1;
    if (/\s/.test(char)) {
      continue;
    }
    if (char !== wanted[matched]) {
      return null;
    }
    matched += 1;
  }
  return name.slice(index).trim();
}

function findColumns({ rows, sheet, issues }: SheetContext): Record<Column, number> | null {
  const headerCol = (label: string) => findLabel(rows, label, { toRow: 0 })?.col;
  const missing = Object.values(COLUMNS).find((label) => headerCol(label) === undefined);
  if (missing) {
    issues.push({
      section: 'dealOptions',
      severity: 'error',
      message: `The "${sheet}" sheet has no "${missing}" column, so no deal options will load.`,
    });
    return null;
  }
  return mapValues(COLUMNS, headerCol);
}

// A base package is named "{nights}N {Room Type}" plus an optional occupancy; anything after that is a variant
// (transfers, upgrades) that the PCM adds on Package Options afterwards.
export function parseDealOptions(context: SheetContext): ParsedDealOptions {
  const columns = findColumns(context);
  if (!columns) {
    return { dealOptions: [], skippedVariants: [] };
  }

  const dealOptions: Array<ParsedDealOption> = [];
  const skippedVariants: Array<string> = [];
  const loadedRowByStay = new Map<string, number>();
  const roomTypeByKey = new Map<string, string>();

  const reject = (row: number, column: Column, message: string) =>
    context.issues.push({
      section: 'dealOptions',
      severity: 'error',
      cell: cellAddress(row, columns[column]),
      row: row + 1,
      message,
    });

  for (let row = 1; row < context.rows.length; row++) {
    const cell = (column: Column) => cellAt(context.rows, row, columns[column]);
    const typedRoomType = textOf(cell('roomType'));
    const sell = priceOf(cell('sell'));
    // The template fills Room Type in for every stay block, so a row nobody priced is template, not a mistake.
    if (sell === null) {
      continue;
    }
    if (!typedRoomType || typedRoomType === '0') {
      reject(row, 'roomType', 'the Room Type is empty.');
      continue;
    }
    const nights = positiveNumberOf(cell('nights'));
    if (nights === null || !Number.isInteger(nights)) {
      reject(row, 'nights', 'the Number of Nights is not a whole number.');
      continue;
    }

    const roomType = typedRoomType.replace(/\s+/g, ' ');
    const name = textOf(cell('name'));
    if (!name) {
      reject(row, 'name', 'the Deal Option Name is empty.');
      continue;
    }
    if (name.startsWith('#')) {
      const error = name.replace(/^#ERROR_/, '');
      reject(row, 'name', `the Deal Option Name shows ${error}. Fix the workbook and upload again.`);
      continue;
    }
    const rest = textAfterPrefix(name, `${nights}N ${roomType}`);
    if (rest === null) {
      reject(row, 'name', `the Deal Option Name doesn't start with "${nights}N ${roomType}".`);
      continue;
    }
    const occupancy = LEADING_OCCUPANCY.exec(rest)?.[0];
    const variant = rest
      .slice(occupancy?.length ?? 0)
      .trim()
      .replace(/\s+/g, ' ');
    if (variant) {
      skippedVariants.push(variant);
      continue;
    }

    const roomKey = squash(roomType);
    const stayKey = `${nights}::${roomKey}`;
    const loadedRow = loadedRowByStay.get(stayKey);
    if (loadedRow !== undefined) {
      reject(row, 'name', `${nights}N ${roomType} is already on row ${loadedRow + 1}.`);
      continue;
    }
    loadedRowByStay.set(stayKey, row);
    if (!roomTypeByKey.has(roomKey)) {
      roomTypeByKey.set(roomKey, roomType);
    }

    const allocation = cell('allocation');
    dealOptions.push({
      row: row + 1,
      roomType: roomTypeByKey.get(roomKey) ?? roomType,
      nights,
      occupancy: occupancy ? occupancyOf(occupancy) : null,
      sell,
      net: priceOf(cell('net')),
      extraNightSell: priceOf(cell('extraNightSell')),
      extraNightNet: priceOf(cell('extraNightNet')),
      allocation: numberOf(allocation)?.toString() ?? textOf(allocation) ?? '',
    });
  }

  return { dealOptions, skippedVariants };
}
